import { useEffect, useMemo, useState } from 'react'
import { MapContainer, GeoJSON, Marker, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'
import type { BranchDto } from '../lib/api'
import 'leaflet/dist/leaflet.css'
import './SteiermarkMiniMap.css'

const GEO_URL = '/geo/steiermark-gemeinden.json'

type GeoFeature = {
  type: 'Feature'
  properties?: { name?: string; iso?: string }
  geometry: unknown
}

type GeoCollection = {
  type: 'FeatureCollection'
  features: GeoFeature[]
}

function normalizeName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function matchBranchCities(branches: BranchDto[], names: string[]): Set<string> {
  const byNorm = new Map<string, string>()
  for (const name of names) byNorm.set(normalizeName(name), name)

  const active = new Set<string>()
  for (const branch of branches) {
    const city = branch.city?.trim()
    if (!city) continue
    const norm = normalizeName(city)
    let gemeinde = byNorm.get(norm)
    if (!gemeinde) {
      for (const [n, full] of byNorm) {
        if (n.includes(norm) || norm.includes(n)) {
          gemeinde = full
          break
        }
      }
    }
    if (gemeinde) active.add(gemeinde)
  }
  return active
}

function centroidOfFeature(feature: GeoFeature): [number, number] | null {
  try {
    const layer = L.geoJSON(feature as GeoJSON.GeoJsonObject)
    const c = layer.getBounds().getCenter()
    return [c.lat, c.lng]
  } catch {
    return null
  }
}

function FitSteiermark({ data, interactive }: { data: GeoCollection; interactive?: boolean }) {
  const map = useMap()
  useEffect(() => {
    const layer = L.geoJSON(data as GeoJSON.GeoJsonObject)
    const bounds = layer.getBounds()
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [12, 12], maxZoom: interactive ? 10 : 9 })
      if (!interactive) {
        const z = map.getZoom()
        map.setMinZoom(z)
        map.setMaxZoom(z)
      }
    }
  }, [data, map, interactive])
  return null
}

function branchPinIcon() {
  return L.divIcon({
    className: 'styria-mini-pin',
    html: '<span class="styria-mini-pin-dot"></span>',
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  })
}

function displayBranchName(name: string): string {
  return name.replace(/^ILIA\s+/i, '').trim() || name
}

type Props = {
  branches: BranchDto[]
  label?: string
  interactive?: boolean
  bookLabel?: string
  bookPath?: (branch: BranchDto) => string
  mapsLabel?: string
  mapsUrl?: (branch: BranchDto) => string
}

export function SteiermarkMiniMap({
  branches,
  label,
  interactive = false,
  bookLabel,
  bookPath,
  mapsLabel,
  mapsUrl,
}: Props) {
  const [geo, setGeo] = useState<GeoCollection | null>(null)

  useEffect(() => {
    let cancelled = false
    void fetch(GEO_URL)
      .then((r) => r.json())
      .then((data: GeoCollection) => {
        if (!cancelled) setGeo(data)
      })
      .catch(() => {
        if (!cancelled) setGeo(null)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const activeGemeinden = useMemo(() => {
    if (!geo) return new Set<string>()
    const names = geo.features.map((f) => String(f.properties?.name ?? '')).filter(Boolean)
    return matchBranchCities(branches, names)
  }, [branches, geo])

  const markers = useMemo(() => {
    if (!geo) return [] as Array<{ branch: BranchDto; lat: number; lng: number }>
    const out: Array<{ branch: BranchDto; lat: number; lng: number }> = []
    for (const branch of branches) {
      if (typeof branch.latitude === 'number' && typeof branch.longitude === 'number') {
        out.push({ branch, lat: branch.latitude, lng: branch.longitude })
        continue
      }
      const city = branch.city?.trim()
      if (!city) continue
      const feature = geo.features.find(
        (f) => normalizeName(String(f.properties?.name ?? '')) === normalizeName(city),
      )
      if (!feature) continue
      const c = centroidOfFeature(feature)
      if (c) out.push({ branch, lat: c[0], lng: c[1] })
    }
    return out
  }, [branches, geo])

  const styleFeature = (feature?: GeoFeature) => {
    const name = String(feature?.properties?.name ?? '')
    const hasBranch = activeGemeinden.has(name)
    if (hasBranch) {
      return {
        color: '#8a6a3d',
        weight: 1.2,
        fillColor: '#c4a574',
        fillOpacity: 0.72,
      }
    }
    return {
      color: 'rgba(138, 106, 61, 0.28)',
      weight: 0.55,
      fillColor: 'rgba(244, 239, 232, 0.72)',
      fillOpacity: 0.42,
    }
  }

  return (
    <div className={`styria-mini-map${interactive ? ' is-interactive' : ''}`} aria-hidden={!geo && !interactive}>
      {!geo && <div className="styria-mini-map-skeleton skeleton" />}
      {geo && (
        <MapContainer
          className="styria-mini-map-leaflet"
          center={[47.25, 15.2]}
          zoom={8}
          zoomControl={interactive}
          scrollWheelZoom={false}
          doubleClickZoom={interactive}
          touchZoom={interactive}
          boxZoom={false}
          keyboard={interactive}
          dragging={interactive}
          attributionControl={false}
        >
          <FitSteiermark data={geo} interactive={interactive} />
          <GeoJSON
            data={geo as GeoJSON.GeoJsonObject}
            style={(f) => styleFeature(f as unknown as GeoFeature)}
          />
          {markers.map(({ branch, lat, lng }) => (
            <Marker key={branch.id} position={[lat, lng]} icon={branchPinIcon()}>
              {interactive && (
                <Popup>
                  <div className="styria-mini-popup">
                    <strong>{displayBranchName(branch.name)}</strong>
                    {(branch.addressLine || branch.city) && (
                      <span>
                        {[branch.addressLine, [branch.postalCode, branch.city].filter(Boolean).join(' ')]
                          .filter(Boolean)
                          .join(', ')}
                      </span>
                    )}
                    {bookLabel && bookPath && (
                      <a className="styria-mini-popup-book" href={bookPath(branch)}>
                        {bookLabel}
                      </a>
                    )}
                    {mapsLabel && mapsUrl && (
                      <a
                        className="styria-mini-popup-maps"
                        href={mapsUrl(branch)}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {mapsLabel}
                      </a>
                    )}
                  </div>
                </Popup>
              )}
            </Marker>
          ))}
        </MapContainer>
      )}
      {label ? <span className="styria-mini-map-label">{label}</span> : null}
    </div>
  )
}
